/**
 * Slack styling constants for pixel-perfect message preview
 */

export const SLACK_COLORS = {
  // Text colors
  text: '#1d1c1d',
  textSecondary: '#616061',
  textMuted: '#868686',

  // Link colors
  link: '#1264a3',
  linkHover: '#0b4c8c',

  // Background colors
  background: '#ffffff',
  messageBg: '#f8f8f8',
  hoverBg: '#f4f4f4',

  // Border colors
  border: '#dddddd',
  borderLight: '#e8e8e8',

  // Brand colors
  avatarBg: '#611f69',
  avatarText: '#ffffff',

  // Code colors
  codeBackground: '#f4f4f4',
  codeBorder: '#e0e0e0',

  // Status colors
  online: '#2bac76',
  offline: '#868686',
};

export const SLACK_FONTS = {
  // Slack's actual font stack - Lato with system font fallbacks
  // Note: For best results, the app includes Lato from Google Fonts
  family: 'Lato, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  monoFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace',
  sizeBase: '13px',
  sizeSmall: '12px',
  sizeTiny: '10px',
  lineHeight: '1.46668',
  lineHeightTight: '1.3',
};

export const SLACK_SPACING = {
  messageGap: '8px',
  avatarSize: '36px',
  avatarGap: '8px',
  blockGap: '4px',
  contextPadding: '8px 0',
};

/**
 * Emoji map for common Slack emoji codes used in Atlas responses
 */
export const EMOJI_MAP: Record<string, string> = {
  // Atlas-specific
  'atlas-light': '\u{1F4D8}', // Blue book
  'atlas': '\u{1F4D8}',

  // Integration sources
  'linear': '\u{1F537}', // Blue diamond
  'slack': '\u{1F4AC}', // Speech bubble
  'notion': '\u{1F4C4}', // Page facing up

  // Common emojis
  'page_facing_up': '\u{1F4C4}',
  'warning': '\u{26A0}\u{FE0F}',
  'white_check_mark': '\u{2705}',
  'x': '\u{274C}',
  'sparkles': '\u{2728}',
  'bulb': '\u{1F4A1}',
  'link': '\u{1F517}',
  'bookmark': '\u{1F516}',
  'pushpin': '\u{1F4CC}',
  'memo': '\u{1F4DD}',
  'clipboard': '\u{1F4CB}',
  'file_folder': '\u{1F4C1}',
  'open_file_folder': '\u{1F4C2}',
  'mag': '\u{1F50D}',
  'mag_right': '\u{1F50E}',
  'gear': '\u{2699}\u{FE0F}',
  'wrench': '\u{1F527}',
  'hammer_and_wrench': '\u{1F6E0}\u{FE0F}',
  'question': '\u{2753}',
  'exclamation': '\u{2757}',
  'information_source': '\u{2139}\u{FE0F}',
  'arrow_right': '\u{27A1}\u{FE0F}',
  'arrow_forward': '\u{25B6}\u{FE0F}',
  'heavy_check_mark': '\u{2714}\u{FE0F}',
  'negative_squared_cross_mark': '\u{274E}',

  // Reactions
  'thumbsup': '\u{1F44D}',
  'thumbsdown': '\u{1F44E}',
  '+1': '\u{1F44D}',
  '-1': '\u{1F44E}',

  // Food (for Atlas restaurant context)
  'pizza': '\u{1F355}',
  'hamburger': '\u{1F354}',
  'fries': '\u{1F35F}',
  'fork_and_knife': '\u{1F374}',
  'knife_fork_plate': '\u{1F37D}\u{FE0F}',
  'receipt': '\u{1F9FE}',

  // Numbers
  'one': '1\u{FE0F}\u{20E3}',
  'two': '2\u{FE0F}\u{20E3}',
  'three': '3\u{FE0F}\u{20E3}',
  'four': '4\u{FE0F}\u{20E3}',
  'five': '5\u{FE0F}\u{20E3}',

  // Colored circles (confidence indicators)
  'large_green_circle': '\u{1F7E2}',
  'large_yellow_circle': '\u{1F7E1}',
  'large_orange_circle': '\u{1F7E0}',
  'large_red_circle': '\u{1F534}',
  'green_circle': '\u{1F7E2}',
  'yellow_circle': '\u{1F7E1}',
  'orange_circle': '\u{1F7E0}',
  'red_circle': '\u{1F534}',
};

/**
 * Get emoji character from Slack emoji code
 */
export function getEmoji(code: string): string {
  const cleanCode = code.replace(/^:|:$/g, '');
  return EMOJI_MAP[cleanCode] || `:${cleanCode}:`;
}

/**
 * Slack Block Kit block types
 */
export interface SlackBlock {
  type: 'section' | 'context' | 'divider' | 'actions' | 'header' | 'image';
  text?: {
    type: 'mrkdwn' | 'plain_text';
    text: string;
  };
  elements?: Array<{
    type: 'mrkdwn' | 'plain_text' | 'button' | 'image';
    text?: string;
    action_id?: string;
    url?: string;
    alt_text?: string;
  }>;
  accessory?: {
    type: string;
    image_url?: string;
    alt_text?: string;
  };
  block_id?: string;
}

/**
 * Citation extracted from admin input
 */
export interface Citation {
  url: string;
  label: string;
  position: number;
}
